// Document Shape Telemetry — founder-only reporting over the aggregate
// document-profiles table. See document-profile-contract.md: every number
// on this page describes a CARD DESIGN shared by millions of holders, never
// a person, a device, or a single scan. The centerpiece question this page
// answers is "is the DDA (REAL ID compliance-type) element actually
// universal, from live data" — see ddaPresencePanel and coverageMatrixPanel.

import { el, fmtInt, fmtPct, fmtDay } from '../ui/dom';
import { renderShell, renderError } from '../ui/shell';
import { kpiCard, kpiGrid, panel, segmented, caption } from '../ui/components';
import { lineAreaChart } from '../ui/charts';
import {
  getDocumentProfileSummary,
  getNewDocumentProfiles,
  type DocumentProfileSummary,
  type DdaPresenceSummary,
  type DdaPresenceRow,
  type CoverageCell,
  type TagFrequencyRow,
  type SubfileDesignatorRow,
  type NewProfileRow,
  type ScanMethod,
} from '../api/document-profiles';
import { UnauthorizedError } from '../api/client';
import { navigate } from '../router';
import type { RouteContext } from '../router';
import {
  classifyCompliance,
  sampleConfidence,
  sampleRowClass,
  pivotCoverageMatrix,
  COMPLIANCE_LABELS,
  COMPLIANCE_PILL_CLASS,
  COMPLIANCE_STATE_ORDER,
  type CoverageMatrixRow,
} from './document-profile-support';

function loadingBox(): HTMLElement {
  return el('div', { class: 'loading' }, ['Loading…']);
}

function emptyNote(text: string): HTMLElement {
  return el('p', { class: 'panel-empty' }, [text]);
}

const SCAN_METHOD_LABELS: Record<ScanMethod, string> = {
  barcode: 'Barcode',
  passport: 'Passport',
  mdl: 'mDL',
};

export async function renderDocumentProfiles({ root }: RouteContext): Promise<void> {
  const content = renderShell(root, 'doc-shapes');

  const head = el('div', { class: 'view-head' }, [
    el('div', { class: 'page-head' }, [el('h1', {}, ['Document Shape Telemetry'])]),
  ]);
  content.append(
    head,
    caption(
      'Aggregate AAMVA/mDL/passport document SHAPES received from contributing scans — card designs shared by ' +
        'millions of holders. Never contents, never per-scan, never per-device; nothing here can identify a ' +
        'holder. Founder-only.',
    ),
  );

  const body = el('div', {}, [loadingBox()]);
  content.append(body);
  content.insertBefore(caption(
    'App 3.0 contributes through a signed-in Check ID account or paired door device. App 3.0.1 adds signed-out personal scans as users update. ' +
      'A Teams subscription is not required. Observations count received profiles; card designs count distinct shapes. ' +
      'Neither is a count of all scans or people.',
  ), body);

  try {
    const summary = await getDocumentProfileSummary();
    body.replaceChildren(renderBody(summary));
  } catch (err) {
    if (err instanceof UnauthorizedError) return navigate('/login');
    renderError(body, err instanceof Error ? err.message : 'Failed to load.');
  }
}

function renderBody(summary: DocumentProfileSummary): HTMLElement {
  if (summary.totalObservations === 0) {
    return el('div', { class: 'empty' }, [
      'No document profiles have been received. Check ID 3.0 sends them from signed-in users and paired door devices; ' +
        '3.0.1 also supports signed-out personal scanning. Supported scans include license barcodes, mobile IDs and passports. ' +
        'Profiles queue locally for upload; offline use, older clients and failed uploads can limit coverage.',
    ]);
  }

  return el('div', { class: 'stack-gap' }, [
    caption(
      `As of ${fmtDay(summary.generatedAt)} · ${fmtInt(summary.totalObservations)} observations across ` +
        `${fmtInt(summary.totalProfiles)} distinct card designs in ${fmtInt(summary.jurisdictionCount)} jurisdictions` +
        (summary.lastReceivedOn ? ` · last received ${fmtDay(summary.lastReceivedOn)}.` : '.'),
    ),
    kpiStrip(summary),
    ddaPresencePanel(summary.ddaPresence),
    coverageMatrixPanel(summary.coverageMatrix),
    tagFrequencyPanel(
      'Element coverage',
      'Every AAMVA/mDL/passport element tag Check ID currently parses, by how widely each appears in the corpus.',
      summary.elementTags,
      false,
    ),
    tagFrequencyPanel(
      'Unparsed tags',
      'Tags observed on real documents that Check ID does not currently parse — directly actionable: this is ' +
        'what the app is leaving on the table.',
      summary.unparsedTags,
      true,
    ),
    subfileDesignatorsPanel(summary.subfileDesignators),
    newProfilesSection(),
  ]);
}

function kpiStrip(summary: DocumentProfileSummary): HTMLElement {
  const overall = summary.ddaPresence.overall;
  const conf = sampleConfidence(overall.observations);
  return kpiGrid([
    kpiCard('Observations', fmtInt(summary.totalObservations)),
    kpiCard('Card designs', fmtInt(summary.totalProfiles), { sub: 'distinct profile shapes seen' }),
    kpiCard('Jurisdictions', fmtInt(summary.jurisdictionCount)),
    kpiCard(
      'DDA presence (overall)',
      conf === 'insufficient' ? '—' : fmtPct(overall.presentRate),
      {
        sub: `${fmtInt(overall.observations)} barcode observations`,
        tone: conf === 'insufficient' ? 'default' : overall.presentRate < 0.5 ? 'warn' : 'ok',
        title:
          'Share of barcode observations where the DDA (compliance-type) element was present at all — ' +
          'NOT the share that asserts REAL ID compliant. Absent and non-compliant are different facts.',
      },
    ),
  ]);
}

// ── Report: DDA presence rate ───────────────────────────────────────────

function ddaPresencePanel(dda: DdaPresenceSummary): HTMLElement {
  const rows = [...dda.byJurisdiction].sort((a, b) => b.observations - a.observations);
  return panel(
    'DDA presence rate by jurisdiction',
    [
      rows.length
        ? el('div', { class: 'table-wrap' }, [
            el('table', { class: 'data-table' }, [
              el('thead', {}, [
                el('tr', {}, [
                  el('th', { textContent: 'Jurisdiction' }),
                  el('th', { class: 'num', textContent: 'Observations' }),
                  el('th', { class: 'num', textContent: 'DDA present' }),
                  el('th', { class: 'num', textContent: 'Absent' }),
                  el('th', { class: 'num', textContent: 'Presence rate' }),
                ]),
              ]),
              el('tbody', {}, rows.map((r) => ddaPresenceRow(r))),
            ]),
          ])
        : emptyNote('No jurisdictions observed yet.'),
    ],
    {
      wide: true,
      subtitle:
        'Whether the DDA element is present AT ALL, not whether it asserts compliant — the live answer to ' +
        '"is DDA universal", per jurisdiction, from your own scans. Rows with few observations are dimmed; ' +
        'trust the raw count over the percentage until it climbs.',
    },
  );
}

function ddaPresenceRow(r: DdaPresenceRow): HTMLElement {
  const conf = sampleConfidence(r.observations);
  return el('tr', { class: sampleRowClass(r.observations) }, [
    el('td', { class: 'cell-strong', textContent: r.jurisdiction }),
    el('td', { class: 'num', textContent: fmtInt(r.observations) }),
    el('td', { class: 'num', textContent: fmtInt(r.present) }),
    el('td', { class: 'num', textContent: fmtInt(r.absent) }),
    el(
      'td',
      { class: 'num' },
      conf === 'insufficient'
        ? [el('span', { class: 'cell-muted', textContent: `n=${r.observations} — too few to rate` })]
        : [
            fmtPct(r.presentRate),
            el('span', { class: 'cell-muted', textContent: ` (n=${fmtInt(r.observations)})` }),
          ],
    ),
  ]);
}

// ── Report: coverage matrix (jurisdiction × AAMVA version × DDA state) ──

type MatrixSort = 'observations' | 'ddaAbsent' | 'jurisdiction';

function coverageMatrixPanel(cells: CoverageCell[]): HTMLElement {
  let sortBy: MatrixSort = 'observations';
  const tableHost = el('div', {});

  function sortedRows(): CoverageMatrixRow[] {
    const rows = pivotCoverageMatrix(cells);
    if (sortBy === 'jurisdiction') {
      return rows.sort(
        (a, b) =>
          a.jurisdiction.localeCompare(b.jurisdiction) || (a.aamvaVersion ?? 0) - (b.aamvaVersion ?? 0),
      );
    }
    if (sortBy === 'ddaAbsent') {
      return rows.sort((a, b) => {
        const rateA = a.total ? a.byState.ABSENT / a.total : 0;
        const rateB = b.total ? b.byState.ABSENT / b.total : 0;
        return rateB - rateA;
      });
    }
    return rows.sort((a, b) => b.total - a.total);
  }

  function renderTable(): void {
    const rows = sortedRows();
    tableHost.replaceChildren(
      rows.length
        ? el('div', { class: 'table-wrap' }, [
            el('table', { class: 'data-table' }, [
              el('thead', {}, [
                el('tr', {}, [
                  el('th', { textContent: 'Jurisdiction' }),
                  el('th', { textContent: 'AAMVA ver.' }),
                  el('th', { class: 'num', textContent: 'Observations' }),
                  ...COMPLIANCE_STATE_ORDER.map((s) =>
                    el('th', { class: 'num', textContent: COMPLIANCE_LABELS[s].split(' — ')[0] }),
                  ),
                ]),
              ]),
              el('tbody', {}, rows.map((r) => matrixRow(r))),
            ]),
          ])
        : emptyNote('No barcode profiles observed yet.'),
    );
  }

  const sortSelect = el('select', { class: 'control' }, [
    el('option', { value: 'observations', textContent: 'Sort: most observed' }),
    el('option', { value: 'ddaAbsent', textContent: 'Sort: highest DDA-absent rate' }),
    el('option', { value: 'jurisdiction', textContent: 'Sort: jurisdiction A–Z' }),
  ]);
  sortSelect.addEventListener('change', () => {
    sortBy = sortSelect.value as MatrixSort;
    renderTable();
  });

  renderTable();

  return panel(
    'Coverage matrix — jurisdiction × AAMVA version × compliance state',
    [el('div', { class: 'controls-right' }, [sortSelect]), tableHost],
    {
      wide: true,
      subtitle:
        'Every observed (jurisdiction, AAMVA version) pair, broken out across all five compliance states. ' +
        'Absent, F, N, M and Other are always separate columns — never summed or collapsed together.',
    },
  );
}

function matrixRow(row: CoverageMatrixRow): HTMLElement {
  return el('tr', { class: sampleRowClass(row.total) }, [
    el('td', { class: 'cell-strong', textContent: row.jurisdiction }),
    el('td', {
      class: 'cell-muted',
      textContent: row.aamvaVersion === null ? '—' : String(row.aamvaVersion),
    }),
    el('td', { class: 'num', textContent: fmtInt(row.total) }),
    ...COMPLIANCE_STATE_ORDER.map((s) => {
      const n = row.byState[s];
      const pct = row.total ? n / row.total : 0;
      return el(
        'td',
        { class: 'num' },
        n === 0
          ? [el('span', { class: 'cell-muted', textContent: '—' })]
          : [
              el('span', { class: COMPLIANCE_PILL_CLASS[s], title: COMPLIANCE_LABELS[s], textContent: String(n) }),
              el('span', { class: 'cell-muted', textContent: ` ${fmtPct(pct, 0)}` }),
            ],
      );
    }),
  ]);
}

// ── Reports: element coverage / unparsed tags (same shape, two slices) ──

function tagFrequencyPanel(
  title: string,
  subtitle: string,
  rows: TagFrequencyRow[],
  actionable: boolean,
): HTMLElement {
  let methodFilter: ScanMethod | 'all' = 'all';
  const filterHost = el('div', {});
  const tableHost = el('div', {});

  function renderTable(): void {
    const filtered = methodFilter === 'all' ? rows : rows.filter((r) => r.scanMethod === methodFilter);
    const sorted = [...filtered].sort((a, b) => b.observations - a.observations);
    tableHost.replaceChildren(
      sorted.length
        ? el('div', { class: 'table-wrap' }, [
            el('table', { class: 'data-table' }, [
              el('thead', {}, [
                el('tr', {}, [
                  el('th', { textContent: 'Tag' }),
                  el('th', { textContent: 'Scan method' }),
                  el('th', { class: 'num', textContent: 'Jurisdictions' }),
                  el('th', { class: 'num', textContent: 'Card designs' }),
                  el('th', { class: 'num', textContent: 'Observations' }),
                  el('th', { textContent: 'First seen' }),
                  el('th', { textContent: 'Last seen' }),
                ]),
              ]),
              el('tbody', {}, sorted.map((r) => tagRow(r))),
            ]),
          ])
        : emptyNote(
            actionable
              ? 'No unparsed tags observed — every element seen so far is one Check ID already parses.'
              : 'No tags observed yet for this scan method.',
          ),
    );
  }

  function renderFilter(): void {
    filterHost.replaceChildren(
      segmented(
        [
          { label: 'All', value: 'all' },
          { label: 'Barcode', value: 'barcode' },
          { label: 'Passport', value: 'passport' },
          { label: 'mDL', value: 'mdl' },
        ],
        methodFilter,
        (v) => {
          methodFilter = v as ScanMethod | 'all';
          renderFilter();
          renderTable();
        },
      ),
    );
  }

  renderFilter();
  renderTable();

  return panel(title, [filterHost, tableHost], { wide: true, subtitle });
}

function tagRow(r: TagFrequencyRow): HTMLElement {
  return el('tr', { class: sampleRowClass(r.observations) }, [
    el('td', { class: 'cell-strong', textContent: r.tag }),
    el('td', {}, [el('span', { class: 'pill', textContent: SCAN_METHOD_LABELS[r.scanMethod] })]),
    el('td', {
      class: 'num',
      title: r.jurisdictions.join(', '),
      textContent: fmtInt(r.jurisdictions.length),
    }),
    el('td', { class: 'num', textContent: fmtInt(r.profiles) }),
    el('td', { class: 'num', textContent: fmtInt(r.observations) }),
    el('td', { class: 'cell-muted', textContent: fmtDay(r.firstSeenOn) }),
    el('td', { class: 'cell-muted', textContent: fmtDay(r.lastSeenOn) }),
  ]);
}

// ── Report: subfile designators seen ────────────────────────────────────

function subfileDesignatorsPanel(rows: SubfileDesignatorRow[]): HTMLElement {
  const sorted = [...rows].sort((a, b) => b.observations - a.observations);
  const hasEn = rows.some((r) => r.subfileType.toUpperCase() === 'EN');
  return panel(
    'Subfile designators seen',
    [
      hasEn
        ? el('div', { class: 'banner banner-warn', role: 'status' }, [
            'An Enhanced (EN) subfile has been observed live — no public source documents this. See the row below.',
          ])
        : null,
      sorted.length
        ? el('div', { class: 'table-wrap' }, [
            el('table', { class: 'data-table' }, [
              el('thead', {}, [
                el('tr', {}, [
                  el('th', { textContent: 'Subfile type' }),
                  el('th', { textContent: 'Scan method' }),
                  el('th', { class: 'num', textContent: 'Jurisdictions' }),
                  el('th', { class: 'num', textContent: 'Card designs' }),
                  el('th', { class: 'num', textContent: 'Observations' }),
                  el('th', { textContent: 'First seen' }),
                  el('th', { textContent: 'Last seen' }),
                ]),
              ]),
              el('tbody', {}, sorted.map((r) => subfileRow(r))),
            ]),
          ])
        : emptyNote('No subfile designators observed yet.'),
    ],
    {
      wide: true,
      subtitle:
        'DL/ID are expected everywhere; anything else — an EN (Enhanced) subfile in particular — is notable on its own.',
    },
  );
}

function subfileRow(r: SubfileDesignatorRow): HTMLElement {
  const isEn = r.subfileType.toUpperCase() === 'EN';
  return el('tr', { class: sampleRowClass(r.observations) }, [
    el('td', {}, [
      el('span', { class: isEn ? 'cell-flag' : 'cell-strong', textContent: r.subfileType }),
    ]),
    el('td', {}, [el('span', { class: 'pill', textContent: SCAN_METHOD_LABELS[r.scanMethod] })]),
    el('td', {
      class: 'num',
      title: r.jurisdictions.join(', '),
      textContent: fmtInt(r.jurisdictions.length),
    }),
    el('td', { class: 'num', textContent: fmtInt(r.profiles) }),
    el('td', { class: 'num', textContent: fmtInt(r.observations) }),
    el('td', { class: 'cell-muted', textContent: fmtDay(r.firstSeenOn) }),
    el('td', { class: 'cell-muted', textContent: fmtDay(r.lastSeenOn) }),
  ]);
}

// ── Report: newly seen profiles over time ───────────────────────────────

const MAX_NEW_PROFILE_ROWS = 200;

function newProfilesSection(): HTMLElement {
  let days = 30;
  const rangeHost = el('div', {});
  const chartHost = el('div', { class: 'chart-host' }, [loadingBox()]);
  const tableHost = el('div', {});

  function renderRange(): void {
    rangeHost.replaceChildren(
      segmented(
        [
          { label: '7 days', value: '7' },
          { label: '30 days', value: '30' },
          { label: '90 days', value: '90' },
        ],
        String(days),
        (v) => {
          days = Number(v);
          renderRange();
          void load();
        },
      ),
    );
  }

  async function load(): Promise<void> {
    chartHost.replaceChildren(loadingBox());
    tableHost.replaceChildren();
    try {
      const result = await getNewDocumentProfiles(days);
      chartHost.replaceChildren(
        result.dailyCounts.length
          ? lineAreaChart(
              result.dailyCounts.map((p) => ({ label: p.date, value: p.newProfiles })),
              { name: 'New card designs', format: (n) => fmtInt(n) },
            )
          : emptyNote(`No new card designs first seen in the last ${days} days.`),
      );
      tableHost.replaceChildren(newProfilesTable(result.items));
    } catch (err) {
      if (err instanceof UnauthorizedError) return navigate('/login');
      chartHost.replaceChildren(
        el('div', { class: 'banner banner-error', role: 'alert' }, [
          err instanceof Error ? err.message : 'Failed to load.',
        ]),
      );
    }
  }

  renderRange();
  void load();

  return panel('Newly seen card designs over time', [rangeHost, chartHost, tableHost], {
    wide: true,
    subtitle:
      'New (jurisdiction, AAMVA version, compliance state, …) shapes entering the corpus for the first time — ' +
      'a proxy for new card designs entering circulation, not new individuals.',
  });
}

function newProfilesTable(items: NewProfileRow[]): HTMLElement {
  if (!items.length) return emptyNote('No new card designs in this window.');
  const shown = items.slice(0, MAX_NEW_PROFILE_ROWS);
  return el('div', {}, [
    el('div', { class: 'table-wrap' }, [
      el('table', { class: 'data-table' }, [
        el('thead', {}, [
          el('tr', {}, [
            el('th', { textContent: 'Scan method' }),
            el('th', { textContent: 'Jurisdiction' }),
            el('th', { textContent: 'AAMVA ver.' }),
            el('th', { textContent: 'Subfile' }),
            el('th', { textContent: 'Compliance' }),
            el('th', { class: 'num', textContent: 'Observations' }),
            el('th', { textContent: 'First seen' }),
          ]),
        ]),
        el('tbody', {}, shown.map((r) => newProfileRow(r))),
      ]),
    ]),
    items.length > MAX_NEW_PROFILE_ROWS
      ? el('p', { class: 'panel-note' }, [
          `Showing the latest ${MAX_NEW_PROFILE_ROWS} of ${fmtInt(items.length)}.`,
        ])
      : null,
  ]);
}

function newProfileRow(r: NewProfileRow): HTMLElement {
  const state = classifyCompliance(r.complianceIndicator);
  return el('tr', {}, [
    el('td', {}, [el('span', { class: 'pill', textContent: SCAN_METHOD_LABELS[r.scanMethod] })]),
    el('td', { class: 'cell-strong', textContent: r.jurisdiction }),
    el('td', {
      class: 'cell-muted',
      textContent: r.aamvaVersion === null ? '—' : String(r.aamvaVersion),
    }),
    el('td', { class: 'cell-muted', textContent: r.subfileType ?? '—' }),
    el('td', {}, [
      el('span', {
        class: COMPLIANCE_PILL_CLASS[state],
        title: COMPLIANCE_LABELS[state],
        textContent: COMPLIANCE_LABELS[state].split(' — ')[0],
      }),
    ]),
    el('td', { class: 'num', textContent: fmtInt(r.observationCount) }),
    el('td', { class: 'cell-muted', textContent: fmtDay(r.firstSeenOn) }),
  ]);
}
